import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, money } from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Copy, Banknote, Printer } from "lucide-react";
import { toast } from "sonner";

export default function OrderConfirmationPage() {
  const { orderNumber } = useParams();
  const navigate = useNavigate();
  const [o, setO] = useState(null);

  useEffect(() => { api.get(`/orders/${orderNumber}`).then((r) => setO(r.data)).catch(() => setO(false)); }, [orderNumber]);

  const copy = (text) => { navigator.clipboard.writeText(text); toast.success("Copied to clipboard"); };

  if (o === false) return <div className="p-20 text-center">Order not found.</div>;
  if (!o) return <div className="p-20 text-center text-muted-foreground">Loading order…</div>;

  const bank = o.bank_details || {};

  return (
    <div className="min-h-screen bg-background flex flex-col"><Header />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 w-full flex-1">
        <div className="text-center">
          <CheckCircle2 className="mx-auto text-emerald-600" size={56} />
          <h1 className="mt-4 text-3xl font-extrabold">Thank you for your order</h1>
          <p className="mt-2 text-muted-foreground max-w-lg mx-auto">{o.confirmation_message}</p>
          <div className="mt-4 inline-block px-5 py-2.5 rounded-lg bg-steel text-accent font-mono text-xl font-bold" data-testid="order-number">
            {o.order_number}
          </div>
          <div className="mt-2 text-sm text-amber-700 font-semibold" data-testid="order-status">Status: {o.status_label}</div>
        </div>

        {/* bank instructions */}
        <div data-testid="bank-instructions" className="mt-8 rounded-xl border-2 border-accent/40 bg-amber-50/50 p-6">
          <div className="flex items-center gap-2 font-heading font-bold text-lg mb-4"><Banknote className="text-accent" /> Bank Transfer Payment Instructions</div>
          <dl className="grid sm:grid-cols-2 gap-3 text-sm">
            <Detail label="Bank" value={bank.bank_name} />
            <Detail label="Account Name" value={bank.account_name} />
            <Detail label="Account Number" value={bank.account_number} mono onCopy={copy} />
            <Detail label="Payment Reference" value={o.payment.reference} mono highlight onCopy={copy} testid="payment-reference" />
          </dl>
          <p className="mt-4 text-sm text-amber-800">Please use <strong className="font-mono">{o.payment.reference}</strong> as your payment reference so we can match your payment quickly.</p>
        </div>

        {/* items */}
        <div className="mt-8 rounded-xl border bg-white p-6">
          <h3 className="font-heading font-bold text-lg mb-4">Order Details</h3>
          <div className="space-y-2 text-sm">
            {o.items.map((i) => (
              <div key={i.sku} className="flex justify-between border-b pb-2">
                <span>{i.qty} × {i.name} <span className="font-mono text-muted-foreground">({i.sku})</span></span>
                <span className="font-mono">{money(i.line_total)}</span>
              </div>
            ))}
            <div className="flex justify-between pt-1"><span>Subtotal (incl GST)</span><span className="font-mono">{money(o.subtotal)}</span></div>
            <div className="flex justify-between"><span>Shipping{o.shipping_info?.method ? ` — ${o.shipping_info.method}` : ""}</span><span className="font-mono" data-testid="order-shipping-amount">{o.free_shipping && o.shipping_info?.status !== "quote_pending" ? "FREE" : money(o.shipping)}{o.shipping_info?.status === "quote_pending" && " + freight (quote pending)"}</span></div>
            {o.shipping_info && <div className="text-xs text-muted-foreground" data-testid="order-shipping-region">Delivery: {o.shipping_info.zone || `${o.shipping_info.island === "north" ? "North" : "South"} Island`}{o.shipping_info.rural ? " · Rural delivery" : ""}</div>}
            {o.shipping_info?.status === "quote_pending" && <div className="text-xs text-red-700" data-testid="order-freight-pending">Freight quotation pending — we'll email you the freight cost to approve. The total below is not final.</div>}
            <div className="flex justify-between text-muted-foreground"><span>GST (15%) included</span><span className="font-mono">{money(o.gst_included)}</span></div>
            <div className="flex justify-between font-bold text-lg border-t pt-2"><span>Total (NZD)</span><span className="font-mono">{money(o.total)}</span></div>
          </div>
          <div className="mt-4 text-sm text-muted-foreground">
            <div><strong>Payment:</strong> Bank Transfer</div>
            <div><strong>Deliver to:</strong> {o.delivery_address.line1}, {o.delivery_address.city} {o.delivery_address.postcode}, {o.delivery_address.country}</div>
          </div>
        </div>

        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <a data-testid="view-invoice-btn" href={`/invoice/${o.order_number}`} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center h-10 px-5 rounded-md bg-accent text-steel hover:bg-amber-600 font-semibold text-sm">
            <Printer size={16} className="mr-2" /> View / Print Tax Invoice
          </a>
          <Button onClick={() => navigate("/")} className="bg-steel text-white hover:bg-steel-light">Continue Shopping</Button>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function Detail({ label, value, mono, highlight, onCopy, testid }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wider text-muted-foreground">{label}</dt>
      <dd data-testid={testid} className={`mt-0.5 flex items-center gap-2 ${mono ? "font-mono" : ""} ${highlight ? "text-accent font-bold" : ""}`}>
        {value}
        {onCopy && <button onClick={() => onCopy(value)} className="text-muted-foreground hover:text-steel"><Copy size={14} /></button>}
      </dd>
    </div>
  );
}
